import React, { useState, useEffect } from 'react';
import { Check, X, Clock, Eye } from 'lucide-react';

const FamillesProposalsAdmin = () => {
  const [proposals, setProposals] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [selectedProposal, setSelectedProposal] = useState(null);

  useEffect(() => {
    loadProposals();
  }, [filter]);

  const loadProposals = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const url = new URL(`${process.env.REACT_APP_BACKEND_URL}/api/admin/familles/propositions`);
      if (filter !== 'all') {
        url.searchParams.set('status', filter);
      }
      
      const response = await fetch(url.toString(), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProposals(data);
      }
    } catch (error) {
      console.error('Erreur chargement propositions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (proposalId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/familles/propositions/${proposalId}/validate`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        alert('Proposition validée avec succès!');
        loadProposals();
      }
    } catch (error) {
      console.error('Erreur validation:', error);
      alert('Erreur lors de la validation');
    }
  };

  const handleReject = async (proposalId) => {
    const reason = prompt('Raison du rejet (optionnel):');
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/familles/propositions/${proposalId}/reject`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ reason })
        }
      );

      if (response.ok) {
        alert('Proposition rejetée');
        loadProposals();
      }
    } catch (error) {
      console.error('Erreur rejet:', error);
      alert('Erreur lors du rejet');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'En attente' },
      validated: { bg: 'bg-green-100', text: 'text-green-800', label: 'Validée' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejetée' }
    };
    const style = styles[status] || styles.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        {style.label}
      </span>
    );
  };

  if (loading) {
    return <div className="p-6">Chargement...</div>;
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
          Propositions Familles et Blasons
        </h2>
        <p className="text-gray-600">Validez ou rejetez les propositions des membres</p>
      </div>

      {/* Filtres */}
      <div className="mb-6 flex gap-2">
        {['pending', 'validated', 'rejected', 'all'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === status
                ? 'text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            style={filter === status ? { backgroundColor: 'rgb(196, 150, 96)' } : {}}
          >
            {status === 'all' ? 'Toutes' : 
             status === 'pending' ? 'En attente' :
             status === 'validated' ? 'Validées' : 'Rejetées'}
          </button>
        ))}
      </div>

      {/* Liste des propositions */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead style={{ backgroundColor: 'rgb(196, 150, 96)' }}>
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase">Nom</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase">Proposé par</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase">Statut</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {proposals.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                  Aucune proposition
                </td>
              </tr>
            ) : (
              proposals.map((proposal) => (
                <tr key={proposal.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm">
                    <span className={`px-2 py-1 rounded text-xs ${
                      proposal.type === 'new' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                    }`}>
                      {proposal.type === 'new' ? 'Nouveau' : 'Modification'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{proposal.name}</div>
                    {proposal.description && (
                      <div className="text-sm text-gray-500 truncate max-w-xs">{proposal.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {proposal.proposed_by_email}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(proposal.created_at).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(proposal.status)}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedProposal(proposal)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        title="Voir détails"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {proposal.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleValidate(proposal.id)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded"
                            title="Valider"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleReject(proposal.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                            title="Rejeter"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal détails */}
      {selectedProposal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setSelectedProposal(null)}>
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full m-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4" style={{ color: 'rgb(196, 150, 96)' }}>
              Détails de la proposition
            </h3>
            <div className="space-y-4">
              <div>
                <label className="font-semibold">Type:</label>
                <p>{selectedProposal.type === 'new' ? 'Nouvelle famille' : 'Modification'}</p>
              </div>
              <div>
                <label className="font-semibold">Nom:</label>
                <p>{selectedProposal.name}</p>
              </div>
              <div>
                <label className="font-semibold">Description:</label>
                <p>{selectedProposal.description || 'Aucune'}</p>
              </div>
              {selectedProposal.image_url && (
                <div>
                  <label className="font-semibold">Blason:</label>
                  <img src={selectedProposal.image_url} alt={selectedProposal.name} className="mt-2 max-w-xs" />
                </div>
              )}
              <div>
                <label className="font-semibold">Proposé par:</label>
                <p>{selectedProposal.proposed_by_email}</p>
              </div>
            </div>
            <div className="mt-6 flex gap-2">
              {selectedProposal.status === 'pending' && (
                <>
                  <button
                    onClick={() => {
                      handleValidate(selectedProposal.id);
                      setSelectedProposal(null);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Valider
                  </button>
                  <button
                    onClick={() => {
                      handleReject(selectedProposal.id);
                      setSelectedProposal(null);
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Rejeter
                  </button>
                </>
              )}
              <button
                onClick={() => setSelectedProposal(null)}
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FamillesProposalsAdmin;
